import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import Box from '@mui/material/Box';
import { RootState } from 'src/redux/rootReducerType';
import { Certification, CertHouseholdData } from 'src/types/Certification';
import HouseholdsList from '../Household/HouseholdsList';
import {
  RENTER_PATHS,
  buildCertificationHubPath,
} from 'src/routes/renterPaths';
import {
  getMemberSurvey,
  memberNeedsActionFromStatus,
} from '../Household/householdSurveyUtils';
import MemberCarousel, { ManagedMember } from './MemberCarousel';
import { setSelectedHomePageMemberId } from 'src/redux/slices/NewRenterInterface/NewRenterInterfaceUserSlice';
import {
  setCertStatus,
  setOpenHouseholdCompositionForCertId,
} from 'src/redux/slices/NewRenterInterface/NewRenterInterfaceSlice';
import ProfileSurveyCard from './ProfileSurveyCard';
import { useProfileSurveyForEmptyState } from './useProfileSurveyForEmptyState';
import { resolveProfileHousehold } from '../HouseholdComposition/profileHouseholdUtils';
import { fetchContinueCertification } from 'src/services/CertificationContinue.service';
import { clearAllDeferred } from 'src/redux/slices/Interview/interviewSlice';
import { userService } from 'src/services/User.service';
import SignatureBanner from './SignatureBanner';
import AppointmentsHomeCard from '../Appointments/AppointmentsHomeCard';
import { certKind } from '../utils/certTerminology';
import CertStatusOverviewModal from '../CertStatus/CertStatusOverviewModal';

function getTimeGreeting(): 'morning' | 'afternoon' | 'evening' {
  const hour = new Date().getHours();
  if (hour >= 5 && hour < 12) {
    return 'morning';
  }
  if (hour >= 12 && hour < 17) {
    return 'afternoon';
  }
  return 'evening';
}

function deriveInitials(firstName: string, lastName: string): string {
  return `${firstName?.[0] ?? ''}${lastName?.[0] ?? ''}`.toUpperCase();
}

/** localStorage key for the once-per-day cert status overview modal. */
const STATUS_OVERVIEW_LAST_SHOWN_KEY = 'pronto.cert-status-overview.lastShown';

const RenterHomePage: FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  /**
   * Cert id whose card is currently resolving its next URL via
   * fetchContinueCertification. Null when no card is loading. Cleared in
   * the finally block so failures don't leave the spinner stuck.
   */
  const [loadingCertId, setLoadingCertId] = useState<number | null>(null);

  const [showStatusOverview, setShowStatusOverview] = useState(false);

  const user = useSelector(
    (state: RootState) => state.newRenterInterfaceUser.user
  );

  const selectedHomePageMemberId = useSelector(
    (state: RootState) =>
      state.newRenterInterfaceUser.accountHub.selectedHomePageMemberId
  );

  const certifications = useSelector(
    (state: RootState) => state.newRenterInterface.certifications
  ) as Certification[];

  const userHouseholds = useSelector(
    (state: RootState) => state.newRenterInterface.userHouseholds
  );

  const unconfirmedHouseholds = useSelector(
    (state: RootState) => state.newRenterInterface.unconfirmedHouseholds ?? []
  );

  const unconfirmedProfileHouseholds = useSelector(
    (state: RootState) =>
      state.newRenterInterface.unconfirmedProfileHouseholds ?? []
  );

  const openHouseholdComposition = useSelector(
    (state: RootState) =>
      state.newRenterInterface.openHouseholdComposition ?? false
  );

  const certStatus = useSelector(
    (state: RootState) => state.newRenterInterface.certStatus ?? {}
  );

  const certStatusLoading = useSelector(
    (state: RootState) => state.newRenterInterface.certStatusLoading
  );

  // Fix 3: use stable certificationIds from Redux state instead of deriving
  // from the certifications array, which gets a new reference on every patch
  // (e.g. patchCertificationsSignatures) and would retrigger the effect.
  const certificationIds = useSelector(
    (state: RootState) => state.newRenterInterface.certificationIds ?? []
  ) as number[];

  // Re-fetch cert status (status text + action items) on home mount so the
  // Status surfaces reflect the latest state after completing the interview
  // (avoids stale data from the initial app-load fetch). Silent background
  // refresh — do not flip a loading flag and flash loaders over
  // already-correct content.
  useEffect(() => {
    if (!certificationIds.length) {
      return;
    }
    let cancelled = false;
    userService
      .fetchCertStatus(certificationIds)
      .then((data) => {
        if (!cancelled) {
          dispatch(setCertStatus(data ?? {}));
        }
      })
      .catch((err) => {
        if (!cancelled) {
          console.error('Failed to refresh cert status', err);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [certificationIds, dispatch]);

  // All active certs for the logged-in user.
  const activeHouseholds = useMemo(
    () =>
      (certifications || [])
        .filter(
          (cert) =>
            cert.cert_household &&
            cert.is_active === true &&
            cert.is_done === false
        )
        .map((cert) => ({
          household: cert.cert_household!,
          property: cert.property,
          certId: cert.id,
          cert,
          categoryName: cert.category?.name,
          created: cert.created,
        })),
    [certifications]
  );

  // Effective member: selected or fall back to logged-in user.
  const effectiveMemberId = selectedHomePageMemberId ?? user?.id ?? null;

  // Once-per-day cert status overview on login (localStorage gate). Opens after
  // household composition resolves (immediately when there's nothing to confirm).
  useEffect(() => {
    if (activeHouseholds.length === 0) {
      return;
    }
    if (unconfirmedHouseholds.length > 0 || openHouseholdComposition) {
      return;
    }
    const today = new Date().toISOString().slice(0, 10);
    if (localStorage.getItem(STATUS_OVERVIEW_LAST_SHOWN_KEY) !== today) {
      localStorage.setItem(STATUS_OVERVIEW_LAST_SHOWN_KEY, today);
      setShowStatusOverview(true);
    }
  }, [
    activeHouseholds.length,
    unconfirmedHouseholds.length,
    openHouseholdComposition,
  ]);

  // Set of UserHousehold ids where the logged-in user holds a managing role
  // (hoh / co_hoh / spouse / helper) — via UHM household_role (Concern A)
  // or via CertificationMember.is_managing (Concern B). Used to authorize
  // "act on behalf of" surfaces: peer certs and peer action dots are only
  // visible when tied to one of these UHs. Self-certs bypass this gate —
  // I always have authority over my own certs regardless of my role in their UH.
  // Helpers are included so a pure helper viewer (no other role anywhere)
  // can still see the household members they're authorized to assist.
  // Pure helpers WITHOUT cert subjects are filtered out downstream in
  // `managedMembers` to avoid showing dead-end peers in the carousel.
  const managedUserHouseholdIds = useMemo<Set<number>>(() => {
    const result = new Set<number>();
    for (const uh of userHouseholds ?? []) {
      if (
        uh.my_role === 'hoh' ||
        uh.my_role === 'co_hoh' ||
        uh.my_role === 'spouse' ||
        uh.my_role === 'helper'
      ) {
        result.add(uh.user_household_id);
      }
    }
    for (const cert of certifications ?? []) {
      const me = cert.cert_household?.cert_members?.find(
        (m) => m.user_id === user?.id && m.is_managing
      );
      if (me && cert.cert_household?.user_household_id != null) {
        result.add(cert.cert_household.user_household_id);
      }
    }
    return result;
  }, [userHouseholds, certifications, user?.id]);

  // Certs filtered to the effective member.
  // A card only renders when (a) the active member is part of the cert
  // household AND (b) that member has a resolvable survey id. Without a
  // survey we have nothing to send the user to once they click — formerly
  // the cert page rendered a stub welcome state for this case; now we
  // simply hide the card. (See removal of RenterCertificationPage.)
  // Additionally, peer certs (effectiveMemberId !== logged-in user) are
  // gated by `managedUserHouseholdIds` so that someone who is e.g. HoH of
  // UH-A and a plain `member` of UH-B cannot act on a peer's cert from
  // UH-B even when both UHs share the same peer user_id.
  const filteredActiveHouseholds = useMemo(() => {
    if (effectiveMemberId == null) {
      return [];
    }
    const isViewingSelf = effectiveMemberId === user?.id;
    return activeHouseholds.filter((item) => {
      const member = item.cert.cert_household?.cert_members?.find(
        (m) => m.user_id === effectiveMemberId
      );
      if (!member) {
        return false;
      }
      if (getMemberSurvey(item.cert, member.cert_member_id) == null) {
        return false;
      }
      // Self always sees their own certs across every UH they belong to.
      // Peer certs require the cert's UH to be in the managed set.
      if (!isViewingSelf) {
        const certUhId = item.cert.cert_household?.user_household_id;
        if (certUhId == null || !managedUserHouseholdIds.has(certUhId)) {
          return false;
        }
      }
      return true;
    });
  }, [activeHouseholds, effectiveMemberId, user?.id, managedUserHouseholdIds]);

  // Build managed members list (logged-in user first, then others).
  // Two sources feed the carousel:
  //   1. UH-based: peers from UserHouseholds where the logged-in user holds
  //      a managing UHM role (hoh / co_hoh / spouse / helper).
  //   2. Cert-based: peers from CertificationHouseholds where the logged-in
  //      user's CertificationMember has a managing member_type — this covers
  //      the case where UHM.household_role is stale but CM.member_type is
  //      authoritative (ENG-2864). Cert-based members are scoped to only the
  //      cert household, not the full UserHousehold.
  // A plain `member` UHM role does not grant act-on-behalf rights, so peers
  // from those UHs are excluded (they may still appear via cert-based source).
  // The logged-in user is always included so the carousel anchors on self.
  //
  // Pure-helper filter: a non-self peer whose ONLY role across the viewer's
  // managed UHs is `helper` is dropped UNLESS they appear as a CertMember
  // on at least one cert visible to the viewer. Helpers are act-on-behalf
  // proxies, not subjects — they only become carousel-relevant when a cert
  // (potentially in a different UH) treats them as a subject. This avoids
  // surfacing dead-end peers (helpers with no surveys to act on) while
  // still honoring the cross-UH carve-out where a helper happens to be a
  // cert subject elsewhere.
  const managedMembers = useMemo<ManagedMember[]>(() => {
    if (!user) {
      return [];
    }
    const result: ManagedMember[] = [
      {
        userId: user.id,
        firstName: user.first_name,
        lastName: user.last_name,
        displayName: user.displayName,
        initials: deriveInitials(user.first_name, user.last_name),
      },
    ];
    const seen = new Set<number>([user.id]);
    // user_ids that have at least one non-helper role across the viewer's
    // managed UHs. Used by the pure-helper filter below.
    const userIdsWithNonHelperRole = new Set<number>();

    for (const uh of userHouseholds ?? []) {
      if (!managedUserHouseholdIds.has(uh.user_household_id)) {
        continue;
      }
      for (const member of uh.members) {
        if (member.user_id == null) {
          continue;
        }
        if (member.household_role !== 'helper') {
          userIdsWithNonHelperRole.add(member.user_id);
        }
        if (seen.has(member.user_id)) {
          continue;
        }
        seen.add(member.user_id);
        result.push({
          userId: member.user_id,
          firstName: member.first_name,
          lastName: member.last_name,
          displayName: `${member.first_name} ${member.last_name}`.trim(),
          initials: deriveInitials(member.first_name, member.last_name),
        });
      }
    }

    // Cert-based source: for each cert where the logged-in user has a
    // managing CM, include that cert's household members. This covers
    // the case where UHM.household_role is stale but the user's CM
    // member_type is authoritative (ENG-2864).
    for (const cert of certifications ?? []) {
      const me = cert.cert_household?.cert_members?.find(
        (m) => m.user_id === user.id && m.is_managing
      );
      if (!me) {
        continue;
      }
      for (const cm of cert.cert_household?.cert_members ?? []) {
        if (cm.user_id == null) {
          continue;
        }
        userIdsWithNonHelperRole.add(cm.user_id);
        if (seen.has(cm.user_id)) {
          continue;
        }
        seen.add(cm.user_id);
        result.push({
          userId: cm.user_id,
          firstName: cm.first_name,
          lastName: cm.last_name,
          displayName: `${cm.first_name} ${cm.last_name}`.trim(),
          initials: deriveInitials(cm.first_name, cm.last_name),
        });
      }
    }

    // user_ids that appear as CertMembers on any visible cert. This is the
    // cross-UH carve-out: a helper who is also a cert subject somewhere we
    // can see is kept in the carousel even when their only UH role is helper.
    const userIdsOnAnyCert = new Set<number>();
    for (const cert of certifications ?? []) {
      for (const cm of cert.cert_household?.cert_members ?? []) {
        if (cm.user_id != null) {
          userIdsOnAnyCert.add(cm.user_id);
        }
      }
    }

    // Pure-helper filter: keep self always; keep peers with at least one
    // non-helper role; keep peers who are cert subjects somewhere visible;
    // drop everyone else (pure helpers without surveys).
    return result.filter((m) => {
      if (m.userId === user.id) {
        return true;
      }
      if (userIdsWithNonHelperRole.has(m.userId)) {
        return true;
      }
      if (userIdsOnAnyCert.has(m.userId)) {
        return true;
      }
      return false;
    });
  }, [userHouseholds, user, managedUserHouseholdIds, certifications]);

  // Carousel renders only when the user has at least one peer they're
  // authorized to act on behalf of. Because `managedMembers` is already
  // scoped to managed UHs, `length > 1` implicitly enforces both
  // "has elevated role" and "has other members" — no separate gate needed.
  const showCarousel = managedMembers.length > 1;

  // Set of userIds that have required actions across any of their active certs.
  // Mirrors the cert-filter authorization rule: action dots only light up for
  // users we can actually act on. Specifically, we only credit a cert's
  // actions to a member when either (a) the member is the logged-in user
  // (we always see our own actions), or (b) the cert's UH is one we manage.
  // Otherwise a peer who appears in the carousel via UH-A could show a red
  // dot driven by an action that lives in UH-B — pointing the user at a
  // cert they have no authority over.
  const membersWithActions = useMemo<Set<number>>(() => {
    const result = new Set<number>();
    for (const cert of certifications || []) {
      if (cert.status?.renter_active === false) {
        continue;
      }
      const certUhId = cert.cert_household?.user_household_id;
      const certIsManaged =
        certUhId != null && managedUserHouseholdIds.has(certUhId);
      for (const member of cert.cert_household?.cert_members ?? []) {
        if (member.user_id == null || result.has(member.user_id)) {
          continue;
        }
        const isSelf = member.user_id === user?.id;
        if (!isSelf && !certIsManaged) {
          continue;
        }
        if (
          memberNeedsActionFromStatus(
            certStatus[cert.id],
            member.user_id,
            member.cert_member_id
          )
        ) {
          result.add(member.user_id);
        }
      }
    }
    return result;
  }, [certifications, certStatus, managedUserHouseholdIds, user?.id]);

  // Greeting always uses the logged-in user's first name, never the selected member.
  const timeGreeting = getTimeGreeting();
  const firstName = user?.first_name ?? '';
  const capitalizedFirstName =
    firstName.charAt(0).toUpperCase() + firstName.slice(1);
  const greeting = `Good ${timeGreeting}, ${capitalizedFirstName}`;

  // Cert count subtitle — varies by whether viewing own certs or another member's.
  const certCount = filteredActiveHouseholds.length;
  const isViewingOwnCerts =
    effectiveMemberId === null || effectiveMemberId === user?.id;

  // Kind-aware label for the active certs: singular uses the cert's kind;
  // plural uses "applications", "re-certifications", or both when mixed.
  const activeKinds = filteredActiveHouseholds.map((item) =>
    certKind(item.cert)
  );
  const hasApplications = activeKinds.some((k) => k === 'application');
  const hasRecerts = activeKinds.some((k) => k === 're-certification');
  const activeCertLabel = (count: number): string => {
    if (count === 1) {
      return activeKinds[0] === 're-certification'
        ? 're-certification'
        : 'application';
    }
    if (hasApplications && hasRecerts) {
      return 'applications and re-certifications';
    }
    return hasRecerts ? 're-certifications' : 'applications';
  };

  const certCountSubtitle = (() => {
    if (isViewingOwnCerts) {
      if (certCount === 0) {
        return 'Keep your profile up to date to get started faster.';
      }
      if (certCount === 1) {
        return `You have 1 active ${activeCertLabel(1)}`;
      }
      return `You have ${certCount} active ${activeCertLabel(certCount)}`;
    }
    const memberName =
      managedMembers.find((m) => m.userId === effectiveMemberId)?.firstName ??
      'This member';
    if (certCount === 0) {
      return `Keep ${memberName}'s profile up to date to get started faster.`;
    }
    if (certCount === 1) {
      return `${memberName} has 1 active ${activeCertLabel(1)}`;
    }
    return `${memberName} has ${certCount} active ${activeCertLabel(
      certCount
    )}`;
  })();

  // Click handler — formerly navigated to RenterCertificationPage which then
  // called fetchContinueCertification on its primary CTA. The intermediate
  // page has been removed; the home page now performs that resolution
  // inline so a click on a cert card lands directly on the interview /
  // data-vault-transfer URL the backend says is next.
  const handleSelectHousehold = useCallback(
    async (household: CertHouseholdData): Promise<void> => {
      const item = activeHouseholds.find(
        (h) => h.household.id === household.id
      );
      if (!item) {
        return;
      }
      const memberId = effectiveMemberId ?? user?.id;
      if (memberId == null) {
        return;
      }
      const targetMember = item.household.cert_members?.find(
        (m) => m.user_id === memberId
      );
      if (!targetMember) {
        return;
      }
      const survey = getMemberSurvey(item.cert, targetMember.cert_member_id);
      if (!survey) {
        // Defensive: filteredActiveHouseholds already excludes this case.
        return;
      }

      // Per WorkItemRenderer/InterviewRoute, the canonical "act on behalf
      // of" identifier across renter routes is User.id. Use the user_id of
      // the resolved member so behavior matches the profile-survey CTA path.
      const isOnBehalf = memberId !== user?.id;
      const onBehalfUserId = targetMember.user_id;

      setLoadingCertId(item.certId);
      try {
        const { redirect_to: redirectTo, survey_started: surveyStarted } =
          await fetchContinueCertification(survey);
        dispatch(clearAllDeferred());
        const base = `/${RENTER_PATHS.HOME}/interview/${survey}`;
        if (redirectTo === 'data-transfer') {
          navigate(
            isOnBehalf && onBehalfUserId != null
              ? `${base}/data-transfer/members/${onBehalfUserId}`
              : `${base}/data-transfer`
          );
          return;
        }
        // ENG-2968: renters with progress land on the certification hub
        // instead of being dropped straight into the interview flow.
        if (surveyStarted && onBehalfUserId != null) {
          navigate(buildCertificationHubPath(item.certId, onBehalfUserId));
          return;
        }
        // New certifications (questionnaire not started): send to files.
        if (!item.cert.questionnaire_started && onBehalfUserId != null) {
          navigate(
            `/${RENTER_PATHS.HOME}/certification/${item.certId}/files/${onBehalfUserId}`
          );
          return;
        }
        navigate(
          isOnBehalf && onBehalfUserId != null
            ? `${base}/members/${onBehalfUserId}`
            : base
        );
      } finally {
        setLoadingCertId(null);
      }
    },
    [navigate, activeHouseholds, effectiveMemberId, user?.id, dispatch]
  );

  const handleLockedContinue = useCallback(
    (household: CertHouseholdData): void => {
      const item = activeHouseholds.find(
        (h) => h.household.id === household.id
      );
      if (!item) {
        return;
      }
      const memberId = effectiveMemberId ?? user?.id;
      if (memberId == null) {
        return;
      }
      const targetMember = item.household.cert_members?.find(
        (m) => m.user_id === memberId
      );
      if (!targetMember?.user_id) {
        return;
      }

      dispatch(clearAllDeferred());
      navigate(buildCertificationHubPath(item.certId, targetMember.user_id));
    },
    [navigate, activeHouseholds, effectiveMemberId, user?.id, dispatch]
  );

  const handleMemberSelect = useCallback(
    (userId: number) => {
      dispatch(
        setSelectedHomePageMemberId(userId === user?.id ? null : userId)
      );
    },
    [dispatch, user?.id]
  );

  const handleAvatarGroupClick = useCallback(
    (cert: Certification) => {
      dispatch(setOpenHouseholdCompositionForCertId(cert.id));
    },
    [dispatch]
  );

  const carousel = showCarousel ? (
    <MemberCarousel
      members={managedMembers}
      selectedUserId={effectiveMemberId ?? user?.id ?? 0}
      onSelect={handleMemberSelect}
      membersWithActions={membersWithActions}
      actionsLoading={certStatusLoading}
    />
  ) : undefined;

  // ENG-3895: next appointment / "Book a visit" sits under the signature
  // banner; the card renders nothing when the home has no online booking.
  const signatureBanner = (
    <>
      <SignatureBanner />
      <Box sx={{ px: 2, pt: 2 }}>
        <AppointmentsHomeCard />
      </Box>
    </>
  );

  // OnBehalfOfBanner copy. We compute this directly from managedMembers
  // (rather than via the useOnBehalfOf hook) because the home page is
  // not scoped to a single cert, while useOnBehalfOf needs a certId to
  // resolve the subject's display name.
  const onBehalfOfName = isViewingOwnCerts
    ? null
    : managedMembers.find((m) => m.userId === effectiveMemberId)?.displayName ??
      null;

  // Profile-survey empty-state CTA: only relevant when the filtered cert
  // list is empty. The hook owns its own dispatch, so calling it
  // unconditionally is fine — it no-ops when isEmpty is false.
  const isEmpty = filteredActiveHouseholds.length === 0;
  const { surveyEntry, actingOnMemberId, proxyMemberFirstName } =
    useProfileSurveyForEmptyState({
      certifications: certifications || [],
      userId: user?.id,
      effectiveMemberId,
      isEmpty,
    });

  const handleProfileSurveyClick = useCallback(() => {
    const surveyId = surveyEntry?.data?.id;
    if (!surveyId) {
      return;
    }
    dispatch(clearAllDeferred());
    if (actingOnMemberId != null) {
      navigate(`/renter/interview/${surveyId}/members/${actingOnMemberId}`);
      return;
    }
    navigate(`/renter/interview/${surveyId}`);
  }, [navigate, surveyEntry?.data?.id, actingOnMemberId, dispatch]);

  // Ordering gate: while a profile household's composition is unconfirmed, the
  // profile-survey CTA must not be actionable — the forced composition modal
  // owns the screen first. Mirrors the cert home's composition-before-status
  // gate. resolveProfileHousehold excludes cert-linked households so a renter
  // mid-certification is never blocked here.
  const profileCompositionPending =
    resolveProfileHousehold(
      unconfirmedProfileHouseholds,
      certifications || []
    ) !== null;

  const profileSurveyEmptyState = isEmpty ? (
    <Box sx={{ px: 2, py: 2 }}>
      <ProfileSurveyCard
        isLoading={
          // Block the CTA while the forced profile-composition modal is up.
          profileCompositionPending ||
          // Stale-while-revalidate: only show loading when we have no data
          // to display. The slice preserves `data` across re-fetches once
          // we've succeeded once, so the CTA stays clickable while a stale
          // entry is being refreshed in the background.
          (!surveyEntry?.data?.id &&
            (surveyEntry === undefined || surveyEntry.status === 'loading'))
        }
        isUnavailable={
          // Only show 'unavailable' when there's no usable data — a failed
          // re-fetch on top of cached data still renders the working CTA.
          !surveyEntry?.data?.id &&
          (surveyEntry?.status === 'failed' ||
            surveyEntry?.status === 'succeeded')
        }
        isProxyMode={actingOnMemberId != null}
        proxyMemberFirstName={proxyMemberFirstName}
        onClick={handleProfileSurveyClick}
      />
    </Box>
  ) : undefined;

  // Stable overview items so the status modal's lazy-fetch effect doesn't
  // re-run on every render (a new array identity each render would re-fetch).
  const statusOverviewItems = useMemo(
    () =>
      filteredActiveHouseholds.map((h) => ({
        cert: h.cert,
        certId: h.certId,
        categoryName: h.categoryName,
        property: h.property,
      })),
    [filteredActiveHouseholds]
  );

  return (
    <>
      <HouseholdsList
        households={filteredActiveHouseholds}
        onSelectHousehold={handleSelectHousehold}
        title={greeting}
        description={certCountSubtitle}
        headerContent={carousel}
        banner={signatureBanner}
        onAvatarGroupClick={handleAvatarGroupClick}
        emptyState={profileSurveyEmptyState}
        loadingCertId={loadingCertId}
        onBehalfOfName={onBehalfOfName}
        onBehalfOfVariant="card"
        onLockedContinue={handleLockedContinue}
      />

      <CertStatusOverviewModal
        open={showStatusOverview}
        onClose={() => {
          setShowStatusOverview(false);
          dispatch(setSelectedHomePageMemberId(null));
        }}
        members={managedMembers}
        selectedUserId={effectiveMemberId ?? user?.id ?? 0}
        onSelectMember={handleMemberSelect}
        membersWithActions={membersWithActions}
        items={statusOverviewItems}
        actionsLoading={certStatusLoading}
      />
    </>
  );
};

export default RenterHomePage;
